import {
  EngagementFeedWithUser,
  NewsfeedFilterType,
  NewsfeedGetRequestDto,
  NewsfeedType,
} from '@epicuro-next/data/engagement/engagement-models';
import {
  getValueFromConfigState,
  selectConfigurationsState,
} from '@epicuro-next/platform/configuration';
import { selectPresetValue } from '@epicuro-next/state/p-basket-state';
import { isBefore, isNonNullish } from '@epicuro-next/utilities';
import { createFeatureSelector, createSelector } from '@ngrx/store';

import {
  NAMESPACE_NEWS_FEED,
  NewsfeedConfigurationKeys,
  NewsfeedConfigurations,
} from '../configuration';

import {
  adapter,
  NewsFeedState,
  NEWS_FEED_STATE_KEY,
} from './news-feed.reducer';

/** News feed feature selector */
export const selectNewsFeed =
  createFeatureSelector<NewsFeedState>(NEWS_FEED_STATE_KEY);

/** Default adapter selectors */
export const { selectAll, selectTotal, selectEntities } =
  adapter.getSelectors(selectNewsFeed);

/** Selector to get loading state value */
export const selectIsLoadingArticles = createSelector(
  selectNewsFeed,
  (state) => state && state.isLoading,
);

export const selectPinned = createSelector(
  selectAll,
  (items: EngagementFeedWithUser[]) =>
    items.filter((item) => item.isPinned).sort(publishedAtCompare),
);

export const selectNotPinned = createSelector(
  selectAll,
  (items: EngagementFeedWithUser[]) =>
    items.filter((item) => !item.isPinned).sort(publishedAtCompare),
);

/** Selector to get list of articles */
export const selectArticles = createSelector(
  selectPinned,
  selectNotPinned,
  (pinned, notPinned) => [...pinned, ...notPinned],
);

/** Selector to get current page */
export const selectPage = createSelector(
  selectNewsFeed,
  (state) => state && state.page,
);

/** Selector to get if all items are loaded */
export const selectAllLoaded = createSelector(
  selectNewsFeed,
  selectTotal,
  (state, articlesLoaded) => {
    return (
      state.total !== null &&
      articlesLoaded >= 0 &&
      state.total === articlesLoaded
    );
  },
);

export const selectLoadFailed = createSelector(
  selectNewsFeed,
  (state) => state.loadFailed,
);

/** Selector to get if all items are loaded */
export const selectFetchParams = createSelector(selectPage, (page) => {
  return isNonNullish(page)
    ? {
        pageSize: 10,
        page: page + 1,
        type: NewsfeedType.Spotlight,
      }
    : null;
});

export const selectActiveReference = createSelector(
  selectNewsFeed,
  (state) => state.activeReference,
);

export const selectActiveArticle = createSelector(
  selectArticles,
  selectActiveReference,
  (articles, activeReference) => {
    if (!activeReference) {
      return null;
    }

    return (
      articles.find(
        (article) =>
          article.referenceId === activeReference.id &&
          article.type === activeReference.type,
      ) ?? null
    );
  },
);

export const selectArticle = (id: string) =>
  createSelector(selectEntities, (articles) => articles[id]);

export function publishedAtCompare(
  a: EngagementFeedWithUser,
  b: EngagementFeedWithUser,
) {
  if (!a.publishedAtUtc || !b.publishedAtUtc) {
    return !a.publishedAtUtc ? 1 : -1;
  }

  return isBefore(new Date(a.publishedAtUtc), new Date(b.publishedAtUtc))
    ? 1
    : -1;
}

/**
 * Selecter to feed fetch query.
 * It waits for rule engine to populate state then returns value
 */
export const selectFetchFeedArguments = createSelector(
  selectConfigurationsState,
  selectFetchParams,
  selectPresetValue,
  (
    configState,
    fetchParams,
    presetValue,
  ): NewsfeedGetRequestDto | undefined => {
    if (!fetchParams) return undefined; // wait news feeed state
    if (!configState[NAMESPACE_NEWS_FEED]) return undefined; // wait rule engine state

    const presetsEnabled = getValueFromConfigState<
      NewsfeedConfigurationKeys,
      NewsfeedConfigurations
    >(
      configState,
      NAMESPACE_NEWS_FEED,
      NewsfeedConfigurationKeys.includePresetsInFiltering,
      false,
    );

    return presetsEnabled
      ? {
          ...fetchParams,
          filterSettings: [
            {
              filterFieldId: NewsfeedFilterType.Preset,
              filterValues: [presetValue as string],
            },
          ],
        }
      : fetchParams;
  },
);
